import { fetchEvent, getEventHints } from '@/services/client.service'
import { TImetaInfo } from '@/types'
import { Event, NostrEvent } from '@nostr/tools/wasm'
import * as kinds from '@nostr/tools/kinds'
import { isReplaceableKind } from '@nostr/tools/kinds'
import { parse } from '@nostr/tools/nip27'
import { AddressPointer, EventPointer, naddrEncode, neventEncode, decode } from '@nostr/tools/nip19'
import { ExternalPointer } from '@nostr/tools/nip22'
import * as nip10 from '@nostr/tools/nip10'
import * as nip22 from '@nostr/tools/nip22'
import { getImetaInfoFromImetaTag, tagNameEquals } from './tag'

export function isReplyNoteEvent(event: Event): boolean {
  if (event.kind === kinds.ShortTextNote) return getParents(event).length > 0
  return event.kind === kinds.Comment || event.kind === kinds.VoiceComment
}

export function isReplaceableEvent(kind: number) {
  if (isNaN(kind)) return false
  return isReplaceableKind(kind) || kinds.isAddressableKind(kind)
}

export function getParents(event?: Event): (ExternalPointer | EventPointer | AddressPointer)[] {
  if (!event) return []

  if (event.kind === kinds.ShortTextNote) {
    let tag = event.tags.find(([tagName, , , marker]) => tagName === 'e' && marker === 'reply')
    if (!tag) {
      const embeddedEventIds = getEmbeddedNoteBech32Ids(event)
      tag = event.tags.findLast(
        ([tagName, tagValue, , marker]) =>
          tagName === 'e' &&
          !!tagValue &&
          marker !== 'mention' &&
          !embeddedEventIds.includes(tagValue)
      )
    }
    if (!tag) return []

    // kind:1 can only have a single parent
    return [{ id: tag[1], author: tag[4], relays: tag[2] ? [tag[2]] : undefined }]
  }

  if (([kinds.Comment, kinds.VoiceComment] as number[]).includes(event.kind)) {
    const parents: ReturnType<typeof getParents> = []

    for (let i = 0; i < event.tags.length; i++) {
      const tag = event.tags[i]
      if (tag[0] === 'i') {
        parents.push({
          value: tag[1],
          hint: tag[2]
        })
      } else if (tag[0] === 'a') {
        const [kindStr, pubkey, ...rest] = tag[1].split(':')
        parents.push({
          kind: parseInt(kindStr, 10),
          pubkey,
          identifier: rest.join(':'),
          relays: tag[2] ? [tag[2]] : undefined
        })
      } else if (tag[0] === 'e') {
        parents.push({ id: tag[1], author: tag[4], relays: tag[2] ? [tag[2]] : undefined })
      }
    }

    return parents
  }

  return []
}

export async function getRoot(
  event: NostrEvent
): Promise<EventPointer | AddressPointer | ExternalPointer | undefined> {
  const { root, reply } =
    event.kind === kinds.ShortTextNote ? nip10.parse(event) : nip22.parse(event)

  if (root) {
    if ('id' in root)
      root.relays = root.relays
        ? [...root.relays, ...getEventHints(root.id)]
        : getEventHints(root.id)
    return root
  }

  if (reply) {
    if ('value' in reply) return reply // ExternalPointer

    // we can't find a root, but we have the immediate parent, so try from there, recursively
    const parent = await fetchEvent(reply)
    if (parent) return getRoot(parent) || parent
  }
}

export function getReplaceableCoordinateFromEvent(event: Event) {
  const d = event.tags.find(tagNameEquals('d'))?.[1]
  return `${event.kind}:${event.pubkey}:${d}`
}

export function getNoteBech32Id(event: Event | AddressPointer | EventPointer) {
  if ('sig' in event) {
    const hints = getEventHints(event.id, event).slice(0, 2)
    if (isReplaceableEvent(event.kind)) {
      const identifier = event.tags.find(tagNameEquals('d'))?.[1] ?? ''
      return naddrEncode({ pubkey: event.pubkey, kind: event.kind, identifier, relays: hints })
    }
    return neventEncode({ id: event.id, author: event.pubkey, kind: event.kind, relays: hints })
  }

  if ('identifier' in event) {
    return naddrEncode(event)
  } else {
    return neventEncode(event)
  }
}

export function getImetaInfosFromEvent(event: Event) {
  const imeta: TImetaInfo[] = []
  for (const tag of event.tags) {
    const imageInfo = getImetaInfoFromImetaTag(tag, event.pubkey)
    if (imageInfo) {
      imeta.push(imageInfo)
    }
  }
  return imeta
}

export function getEmbeddedNoteBech32Ids(event: Event) {
  const embeddedNoteBech32Ids: string[] = []
  const embeddedNoteRegex = /nostr:(note1[a-z0-9]{58}|nevent1[a-z0-9]+)/g
  for (const note of event.content.match(embeddedNoteRegex) || []) {
    try {
      const { type, data } = decode(note.split(':')[1])
      if (type === 'nevent') {
        embeddedNoteBech32Ids.push(data.id)
      } else if (type === 'note') {
        embeddedNoteBech32Ids.push(data)
      }
    } catch {
      // ignore
    }
  }
  return embeddedNoteBech32Ids
}

export function eventMentionsPubKeyInContent(event: Event, pubkey: string) {
  for (const block of parse(event)) {
    if (
      block.type === 'reference' &&
      'pubkey' in block.pointer &&
      !('identifier' in block.pointer) &&
      block.pointer.pubkey === pubkey
    ) {
      return true
    }
  }

  return false
}

export async function findFirstDirectReplyToPubkey(
  event: NostrEvent,
  pubkey: string
): Promise<EventPointer | AddressPointer | undefined> {
  const parents = getParents(event)

  const index = (
    await Promise.all(
      parents.map(async (parent) => {
        if (parent && 'id' in parent) {
          const parentAuthorFromTag = parent.author
          if (parentAuthorFromTag === pubkey) {
            return true
          }

          // If no hint or hint doesn't match, fetch the parent event
          if (!parentAuthorFromTag) {
            try {
              const parentEvent = await fetchEvent(parent.id)
              if (parentEvent && parentEvent.pubkey === pubkey) {
                return true
              }
            } catch (e) {
              console.debug('Could not fetch parent event for filtering:', e)
            }
          }
        }

        return false
      })
    )
  ).findIndex((v) => v)

  if (index === -1) return undefined

  return parents[index] as EventPointer | AddressPointer
}

export function getReplaceableEventIdentifier(event: Event) {
  return event.tags.find(tagNameEquals('d'))?.[1] ?? ''
}

export function createFakeEvent(event: Partial<Event>): Event {
  return {
    id: '',
    kind: 1,
    pubkey: '',
    content: '',
    created_at: 0,
    tags: [],
    sig: '',
    ...event
  }
}

// Legacy compare function for sorting compatibility
// If return 0, it means the two events are equal.
// If return a negative number, it means `b` should be retained, and `a` should be discarded.
// If return a positive number, it means `a` should be retained, and `b` should be discarded.
export function compareEvents(a: Event, b: Event): number {
  if (a.created_at !== b.created_at) {
    return a.created_at - b.created_at
  }
  // In case of replaceable events with the same timestamp, the event with the lowest id (first in lexical order) should be retained, and the other discarded.
  if (a.id !== b.id) {
    return a.id < b.id ? 1 : -1
  }
  return 0
}

export function arePointersToSameEvent(
  a: ExternalPointer | EventPointer | AddressPointer,
  b: ExternalPointer | EventPointer | AddressPointer
): boolean {
  return (
    ('id' in b && 'id' in a && b.id === a.id) ||
    ('identifier' in b &&
      'identifier' in a &&
      b.identifier === a?.identifier &&
      b.pubkey === a?.pubkey) ||
    ('value' in b && 'value' in a && b.value === a.value) ||
    false
  )
}
